import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import swal from "sweetalert";
import "./ScanSetting.scss";

// Detection modules. featureID values and the field each Check stores its value in
// (Keyword vs Port) must match what the backend stores in ScanProfile.
const MODULES = [
    { key: "credentials", name: "Exposed Credentials", featureID: 165112129704651, field: "Keyword", col: "String", placeholder: "name@company.com" },
    { key: "subdomains", name: "Exposed Subdomain", featureID: 165112129704687, field: "Keyword", col: "String", placeholder: "keyword" },
    { key: "whois", name: "Exposed WHOIS", featureID: 165112129704697, field: "Keyword", col: "String", placeholder: "name@company.com" },
    { key: "dns", name: "Exposed DNS", featureID: 165112129704677, field: "Keyword", col: "String", placeholder: "192.168.1.1" },
    { key: "ports", name: "Exposed Ports", featureID: 165112129704796, field: "Port", col: "Port", placeholder: "443" },
    { key: "reverseDNS", name: "Exposed ReverseDNS", featureID: 165112129704189, field: "Keyword", col: "String", placeholder: "host.company.com" },
    { key: "reverseIP", name: "Exposed ReverseIP", featureID: 165112129704642, field: "Keyword", col: "String", placeholder: "host.company.com" },
];

const RISKS = [
    { value: "Critical", label: "Critical", solid: "var(--sev-critical)", tint: "var(--red-100)", fg: "var(--red-600)" },
    { value: "High", label: "High", solid: "var(--sev-high)", tint: "var(--orange-100)", fg: "var(--orange-600)" },
    { value: "Medium", label: "Medium", solid: "var(--sev-medium)", tint: "var(--amber-100)", fg: "var(--amber-600)" },
    { value: "Low", label: "Low", solid: "var(--sev-low)", tint: "var(--blue-100)", fg: "var(--blue-700)" },
    { value: "Informational", label: "Info", solid: "var(--sev-info)", tint: "var(--sev-info-tint)", fg: "var(--slate-500)" },
];
const riskMeta = (risk) => RISKS.find((r) => r.value === risk) || RISKS[4];

const ChevronIcon = ({ open }) => (
    <svg className="chev" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ transform: `rotate(${open ? 90 : 0}deg)` }}>
        <path d="m9 18 6-6-6-6" />
    </svg>
);
const TrashIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
);
const PlusIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <path d="M12 5v14M5 12h14" />
    </svg>
);

const RiskSegment = ({ value, onChange }) => (
    <div className="ss-segment" role="radiogroup" aria-label="Risk">
        {RISKS.map((r) => {
            const active = value === r.value;
            return (
                <button
                    key={r.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    title={r.value}
                    className={active ? "active" : ""}
                    style={active ? { background: r.solid } : undefined}
                    onClick={() => onChange(r.value)}
                >
                    {r.label}
                </button>
            );
        })}
    </div>
);

const ScanSetting = () => {
    const userID = localStorage.getItem("userID");
    const orgID = localStorage.getItem("orgID");
    const token = localStorage.getItem("token");

    const { layoutMode } = useSelector((state) => state.Layout || { layoutMode: "dark" });
    const theme = layoutMode === "light" ? "light" : "dark";

    // { [moduleKey]: { scanProfileID, checks: [] } } — exactly what the backend returns
    const [profiles, setProfiles] = useState({});
    const [loaded, setLoaded] = useState(false);
    // Pending (unsaved) risk edits: { [moduleKey]: { [checkID]: risk } }
    const [pendingRisk, setPendingRisk] = useState({});
    // Draft rows created with ADD: { [moduleKey]: [{ tmpId, value, risk }] }
    const [drafts, setDrafts] = useState({});
    const [busy, setBusy] = useState({}); // { [rowId]: true } while a request is in flight
    const [feedback, setFeedback] = useState(null); // { id, ok }
    const [openMap, setOpenMap] = useState({ credentials: true });
    // UI-only switch, same as the previous page (not persisted to the backend)
    const [enabled, setEnabled] = useState(() => Object.fromEntries(MODULES.map((m) => [m.key, true])));

    // Api to hit backend for getting the scan profile data of one module
    const fetchModule = useCallback(async (mod) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_DEFAULTPATH}updatesubdomaintable`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ userID, orgID, featureIDForServer: mod.featureID }),
            });
            const data = await response.json();
            if (data.data) {
                const next = { scanProfileID: data.data.ScanProfileID, checks: data.data.Checks || [] };
                setProfiles((p) => ({ ...p, [mod.key]: next }));
                return next;
            }
        } catch (error) {
            // keep previous data on failure
        }
        return null;
    }, [userID, orgID]);

    // Api to update / delete / add a check (unchanged request body)
    async function updateString(string, id, riskValue, index, stringID, taskType) {
        const response = await fetch(`${process.env.REACT_APP_DEFAULTPATH}updatestring`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ stringID, string, id, riskValue, index, taskType }),
        });
        return response.json();
    }

    useEffect(() => {
        (async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_DEFAULTPATH}jwtauth`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ token }),
                });
                const data = await response.json();
                if (!data.status) {
                    window.location.href = "/login";
                    return;
                }
            } catch (error) {
                // fall through and still try to load settings
            }
            await Promise.all(MODULES.map((m) => fetchModule(m)));
            setLoaded(true);
        })();
    }, [token, fetchModule]);

    const flash = (id, ok) => {
        setFeedback({ id, ok });
        clearTimeout(flash.t);
        flash.t = setTimeout(() => setFeedback(null), 1800);
    };
    const setBusyFor = (id, v) => setBusy((b) => ({ ...b, [id]: v }));

    const saveRisk = async (mod, check, index) => {
        const newRisk = pendingRisk[mod.key] && pendingRisk[mod.key][check.ID];
        if (!newRisk || newRisk === check.Risk) return;
        const profile = profiles[mod.key];
        setBusyFor(check.ID, true);
        try {
            await updateString(check[mod.field], profile.scanProfileID, newRisk, index, check.ID, "updateRisk");
        } catch (e) {
            // the response status is not reliable for updateRisk; verify by reloading below
        }
        const fresh = await fetchModule(mod);
        const saved = fresh && fresh.checks.find((c) => c.ID === check.ID);
        const ok = !!saved && saved.Risk === newRisk;
        if (ok) {
            setPendingRisk((p) => {
                const m = { ...(p[mod.key] || {}) };
                delete m[check.ID];
                return { ...p, [mod.key]: m };
            });
        }
        setBusyFor(check.ID, false);
        flash(check.ID, ok);
    };

    const deleteCheck = async (mod, check, index) => {
        const ok = await swal({
            title: "Delete rule?",
            text: `Are you sure you want to delete "${check[mod.field]}" from ${mod.name}?`,
            icon: "warning",
            buttons: ["Cancel", "Delete"],
            dangerMode: true,
        });
        if (!ok) return;
        const profile = profiles[mod.key];
        setBusyFor(check.ID, true);
        try {
            await updateString(check[mod.field], profile.scanProfileID, check.Risk, index, check.ID, "delete");
        } catch (e) {
            swal("Error", "Could not delete the rule. Please try again.", "error");
        }
        await fetchModule(mod);
        setBusyFor(check.ID, false);
    };

    const addDraft = (mod) => {
        const tmpId = `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        setDrafts((d) => ({ ...d, [mod.key]: [...(d[mod.key] || []), { tmpId, value: "", risk: "Medium" }] }));
        setOpenMap((o) => ({ ...o, [mod.key]: true }));
    };
    const updateDraft = (mod, tmpId, patch) =>
        setDrafts((d) => ({ ...d, [mod.key]: (d[mod.key] || []).map((r) => (r.tmpId === tmpId ? { ...r, ...patch } : r)) }));
    const removeDraft = (mod, tmpId) =>
        setDrafts((d) => ({ ...d, [mod.key]: (d[mod.key] || []).filter((r) => r.tmpId !== tmpId) }));

    const saveDraft = async (mod, draft) => {
        const value = draft.value.trim();
        if (!value) return;
        if (mod.field === "Port" && !/^\d{1,5}$/.test(value)) {
            swal("Invalid port", "Please enter a numeric port (e.g. 443).", "warning");
            return;
        }
        const profile = profiles[mod.key];
        if (!profile || !profile.scanProfileID) {
            swal("Error", "Scan profile for this module was not found.", "error");
            return;
        }
        setBusyFor(draft.tmpId, true);
        let res = null;
        try {
            res = await updateString(value, profile.scanProfileID, draft.risk, 6, undefined, "addString");
        } catch (e) {
            res = null;
        }
        setBusyFor(draft.tmpId, false);
        if (res && res.status === "ok") {
            removeDraft(mod, draft.tmpId);
            await fetchModule(mod);
        } else {
            swal("Error", "Could not add the rule. Please try again.", "error");
        }
    };

    const toggleOpen = (key) => setOpenMap((o) => ({ ...o, [key]: !o[key] }));
    const toggleEnabled = (key) => setEnabled((e) => ({ ...e, [key]: !e[key] }));

    return (
        <React.Fragment>
            <div className="page-content scan-settings-root" data-theme={theme}>
                <div className="ss-container">
                    <div className="ss-header">
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            <h1>Scan Settings</h1>
                            <span className="ss-subtitle">Detection modules and their rules. Select a module to edit its rules.</span>
                        </div>
                        <div className="ss-breadcrumb">
                            <Link to="/dashboard">Dashboard</Link>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 18 6-6-6-6" /></svg>
                            <span className="current">Scan settings</span>
                        </div>
                    </div>

                    <section className="ss-card">
                        <div className="ss-grid ss-head-row">
                            <span>Module</span>
                            <span>Rules</span>
                            <span className="ss-col-mix">Risk mix</span>
                            <span className="ss-col-breakdown">Breakdown</span>
                            <span>Status</span>
                            <span style={{ textAlign: "right" }}>Enabled</span>
                        </div>

                        {MODULES.map((mod) => {
                            const profile = profiles[mod.key] || { checks: [] };
                            const checks = profile.checks || [];
                            const modDrafts = drafts[mod.key] || [];
                            const modPending = pendingRisk[mod.key] || {};
                            const isOn = enabled[mod.key];
                            const open = !!openMap[mod.key];

                            const dirtyCount =
                                checks.filter((c) => modPending[c.ID] && modPending[c.ID] !== c.Risk).length +
                                modDrafts.filter((d) => d.value.trim()).length;
                            const total = checks.length || 1;
                            const summary = RISKS.map((r) => ({
                                ...r,
                                count: checks.filter((c) => riskMeta(c.Risk).value === r.value).length,
                            })).filter((s) => s.count);

                            let statusText = isOn ? "Active" : "Off";
                            let statusColor = isOn ? "var(--green-600)" : "var(--fg-tertiary)";
                            if (!loaded && !profiles[mod.key]) {
                                statusText = "Loading…";
                                statusColor = "var(--fg-tertiary)";
                            } else if (dirtyCount) {
                                statusText = `${dirtyCount} unsaved`;
                                statusColor = "var(--amber-600)";
                            }

                            return (
                                <div className="ss-module" key={mod.key}>
                                    <div className={`ss-grid ss-module-row ${open ? "open" : ""}`} onClick={() => toggleOpen(mod.key)}>
                                        <span className={`ss-module-name ${isOn ? "" : "off"}`}>
                                            <ChevronIcon open={open} />
                                            {mod.name}
                                        </span>
                                        <span className="ss-mono">{checks.length}</span>
                                        <div className={`ss-col-mix ${isOn ? "" : "ss-dim"}`}>
                                            <div className="ss-mix-bar">
                                                {summary.map((s) => (
                                                    <div key={s.value} title={`${s.count} ${s.value}`} style={{ width: `${(s.count / total) * 100}%`, background: s.solid }} />
                                                ))}
                                            </div>
                                        </div>
                                        <div className={`ss-pills ss-col-breakdown ${isOn ? "" : "ss-dim"}`}>
                                            {summary.map((s) => (
                                                <span key={s.value} className="ss-pill" style={{ background: s.tint, color: s.fg }}>
                                                    {s.count} {s.label}
                                                </span>
                                            ))}
                                        </div>
                                        <span className="ss-status" style={{ color: statusColor }}>{statusText}</span>
                                        <div style={{ display: "flex", justifyContent: "flex-end" }} onClick={(e) => e.stopPropagation()}>
                                            <button
                                                type="button"
                                                role="switch"
                                                aria-checked={isOn}
                                                aria-label={`${mod.name} enabled`}
                                                className={`ss-switch ${isOn ? "on" : ""}`}
                                                onClick={() => toggleEnabled(mod.key)}
                                            >
                                                <span className="knob" />
                                            </button>
                                        </div>
                                    </div>

                                    {open && (
                                        <div className={`ss-rules ${isOn ? "" : "ss-dim"}`}>
                                            <div className="ss-rules-grid ss-rules-head">
                                                <span>{mod.col}</span>
                                                <span>Risk</span>
                                                <span style={{ textAlign: "right" }}>Action</span>
                                            </div>

                                            {checks.length === 0 && modDrafts.length === 0 && (
                                                <div className="ss-empty">
                                                    {loaded ? "No rules yet. Use ADD to create one." : "Loading rules…"}
                                                </div>
                                            )}

                                            {checks.map((check, index) => {
                                                const current = modPending[check.ID] || check.Risk;
                                                const dirty = !!modPending[check.ID] && modPending[check.ID] !== check.Risk;
                                                const isBusy = !!busy[check.ID];
                                                const fb = feedback && feedback.id === check.ID ? feedback : null;
                                                return (
                                                    <div className="ss-rules-grid ss-rule-row" key={check.ID || index}>
                                                        <span className="ss-rule-value" title={String(check[mod.field])}>{check[mod.field]}</span>
                                                        <RiskSegment
                                                            value={riskMeta(current).value}
                                                            onChange={(v) =>
                                                                setPendingRisk((p) => ({ ...p, [mod.key]: { ...(p[mod.key] || {}), [check.ID]: v } }))
                                                            }
                                                        />
                                                        <div className="ss-actions">
                                                            {fb && fb.ok && !dirty && <span className="saved">Saved</span>}
                                                            {fb && !fb.ok && <span className="failed">Failed</span>}
                                                            <button
                                                                type="button"
                                                                className={`ss-btn-save ${dirty ? "dirty" : ""}`}
                                                                disabled={!dirty || isBusy}
                                                                onClick={() => saveRisk(mod, check, index)}
                                                            >
                                                                {isBusy && dirty ? "SAVING" : "SAVE"}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="ss-btn-icon"
                                                                aria-label="Delete"
                                                                title="Delete"
                                                                disabled={isBusy}
                                                                onClick={() => deleteCheck(mod, check, index)}
                                                            >
                                                                <TrashIcon />
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                            {modDrafts.map((draft) => {
                                                const dirty = !!draft.value.trim();
                                                const isBusy = !!busy[draft.tmpId];
                                                return (
                                                    <div className="ss-rules-grid ss-rule-row is-new" key={draft.tmpId}>
                                                        <input
                                                            className="ss-input"
                                                            autoFocus
                                                            value={draft.value}
                                                            placeholder={`e.g. ${mod.placeholder}`}
                                                            inputMode={mod.field === "Port" ? "numeric" : undefined}
                                                            onChange={(e) => updateDraft(mod, draft.tmpId, { value: e.target.value })}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Enter") saveDraft(mod, draft);
                                                                if (e.key === "Escape") removeDraft(mod, draft.tmpId);
                                                            }}
                                                        />
                                                        <RiskSegment value={draft.risk} onChange={(v) => updateDraft(mod, draft.tmpId, { risk: v })} />
                                                        <div className="ss-actions">
                                                            <button
                                                                type="button"
                                                                className={`ss-btn-save ${dirty ? "dirty" : ""}`}
                                                                disabled={!dirty || isBusy}
                                                                onClick={() => saveDraft(mod, draft)}
                                                            >
                                                                {isBusy ? "SAVING" : "SAVE"}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="ss-btn-icon"
                                                                aria-label="Discard"
                                                                title="Discard"
                                                                disabled={isBusy}
                                                                onClick={() => removeDraft(mod, draft.tmpId)}
                                                            >
                                                                <TrashIcon />
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                            <div className="ss-rules-footer">
                                                <span>
                                                    {dirtyCount
                                                        ? `${dirtyCount} unsaved change${dirtyCount > 1 ? "s" : ""}`
                                                        : `${checks.length} rule${checks.length === 1 ? "" : "s"}`}
                                                </span>
                                                <button type="button" className="ss-btn-primary" onClick={() => addDraft(mod)}>
                                                    <PlusIcon /> ADD
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </section>
                </div>
            </div>
        </React.Fragment>
    );
};

export default ScanSetting;
